/**
 * Inline Image Handling for Email Composer
 *
 * Handles:
 * - Extracting images from HTML content
 * - Converting data URLs to CID references
 * - Building multipart/related MIME structure
 */

export interface InlineImage {
  cid: string;
  dataUrl: string;
  mimeType: string;
  base64Data: string;
  filename: string;
}

/**
 * Extract inline images from HTML content and replace with CID references
 */
export function extractInlineImages(html: string): {
  processedHtml: string;
  images: InlineImage[];
} {
  const images: InlineImage[] = [];
  let imageCounter = 0;

  // Match img tags with data URL src
  const imgRegex = /<img[^>]+src="(data:([^;]+);base64,([^"]+))"[^>]*>/gi;

  const processedHtml = html.replace(imgRegex, (match, dataUrl, mimeType, base64Data) => {
    imageCounter++;
    const cid = `image${String(imageCounter).padStart(3, '0')}@buildit.local`;
    const extension = mimeType.split('/')[1] || 'png';
    const filename = `image${imageCounter}.${extension}`;

    images.push({
      cid,
      dataUrl,
      mimeType,
      base64Data,
      filename,
    });

    // Replace data URL with CID reference
    return match.replace(dataUrl, `cid:${cid}`);
  });

  return { processedHtml, images };
}

/**
 * Check if HTML content contains inline images
 */
export function hasInlineImages(html: string): boolean {
  return /src="data:image\/[^;]+;base64,/.test(html);
}

/**
 * Build multipart/related MIME message with inline images
 */
export function buildMimeWithInlineImages(
  headers: string[],
  htmlBody: string,
  images: InlineImage[]
): string {
  const boundary = `related_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const parts: string[] = [];

  // Add headers
  parts.push(...headers);
  parts.push(`Content-Type: multipart/related; boundary="${boundary}"`);
  parts.push('MIME-Version: 1.0');
  parts.push('');

  // HTML body part
  parts.push(`--${boundary}`);
  parts.push('Content-Type: text/html; charset=utf-8');
  parts.push('Content-Transfer-Encoding: 7bit');
  parts.push('');
  parts.push(htmlBody);
  parts.push('');

  // Image parts
  for (const image of images) {
    parts.push(`--${boundary}`);
    parts.push(`Content-Type: ${image.mimeType}`);
    parts.push(`Content-ID: <${image.cid}>`);
    parts.push('Content-Transfer-Encoding: base64');
    parts.push(`Content-Disposition: inline; filename="${image.filename}"`);
    parts.push('');
    parts.push(image.base64Data);
    parts.push('');
  }

  // Closing boundary
  parts.push(`--${boundary}--`);

  return parts.join('\r\n');
}

/**
 * Build multipart/mixed message with both attachments and inline images
 */
export function buildMimeWithAttachmentsAndImages(
  headers: string[],
  htmlBody: string,
  images: InlineImage[],
  attachments: Array<{ name: string; type: string; base64: string }>
): string {
  const mixedBoundary = `mixed_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const relatedBoundary = `related_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const parts: string[] = [];

  // Add headers
  parts.push(...headers);
  parts.push(`Content-Type: multipart/mixed; boundary="${mixedBoundary}"`);
  parts.push('MIME-Version: 1.0');
  parts.push('');

  // Start with multipart/related for HTML + inline images
  parts.push(`--${mixedBoundary}`);
  parts.push(`Content-Type: multipart/related; boundary="${relatedBoundary}"`);
  parts.push('');

  // HTML body part
  parts.push(`--${relatedBoundary}`);
  parts.push('Content-Type: text/html; charset=utf-8');
  parts.push('Content-Transfer-Encoding: 7bit');
  parts.push('');
  parts.push(htmlBody);
  parts.push('');

  // Inline image parts
  for (const image of images) {
    parts.push(`--${relatedBoundary}`);
    parts.push(`Content-Type: ${image.mimeType}`);
    parts.push(`Content-ID: <${image.cid}>`);
    parts.push('Content-Transfer-Encoding: base64');
    parts.push(`Content-Disposition: inline; filename="${image.filename}"`);
    parts.push('');
    parts.push(image.base64Data);
    parts.push('');
  }

  // Close related boundary
  parts.push(`--${relatedBoundary}--`);
  parts.push('');

  // Attachment parts
  for (const attachment of attachments) {
    parts.push(`--${mixedBoundary}`);
    parts.push(`Content-Type: ${attachment.type}; name="${attachment.name}"`);
    parts.push(`Content-Disposition: attachment; filename="${attachment.name}"`);
    parts.push('Content-Transfer-Encoding: base64');
    parts.push('');
    parts.push(attachment.base64);
    parts.push('');
  }

  // Close mixed boundary
  parts.push(`--${mixedBoundary}--`);

  return parts.join('\r\n');
}

/**
 * Validate image before insertion
 */
export function validateImage(file: File): { valid: boolean; error?: string } {
  const MAX_SIZE = 2 * 1024 * 1024; // 2MB per image
  const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: `Unsupported image type: ${file.type}` };
  }

  if (file.size > MAX_SIZE) {
    return { valid: false, error: 'Image must be smaller than 2MB' };
  }

  return { valid: true };
}

/**
 * Convert File to base64 data URL
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
